import { ArrowLeft, MessageCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ApiError, type ApiClient } from '@despachador/api-client';
import { AUTH_ERRORS } from '@despachador/contracts/auth-errors';
import { PASSWORD_RULES } from '@despachador/domain';
import { Notice, useNow } from '../index';
import { AuthCard, authMessage, CodeInput, countdown, errorCode, IdentifierField, NewPassword, retryAfterSeconds, TenantField, waitText, type AuthBrand } from './pieces';

/** Routes of the recovery (docs/authentication.md «Recuperación de contraseña»). */
export const RECOVERY_PATHS = {
  start: '/acceso/recuperar',
  code: '/acceso/recuperar/codigo',
  password: '/acceso/nueva-clave',
  done: '/acceso/recuperar/listo',
} as const;
export type RecoveryStep = keyof typeof RECOVERY_PATHS;

/**
 * The flow kept in memory by the gate across its routes. Never in the URL or in storage: a reload
 * starts over (and says so).
 */
export interface RecoveryState {
  identifier: string;
  tenant: string | null;
  challengeId: string | null;
  grant: string | null;
  done: boolean;
  /** When the last code was requested (ms), to pace "Enviar otro código". */
  sentAt: number;
  /** When the current code stops working (ms): the API may answer the code still valid, with less than 10 min left. */
  expiresAt: number;
  /**
   * The flow started in this page (memory). A reload loses it and the later steps start over; a step
   * that ends (or expires) keeps it while the router moves to the next route.
   */
  alive: boolean;
}

export const emptyRecovery = (identifier = '', alive = false): RecoveryState => ({ identifier, tenant: null, challengeId: null, grant: null, done: false, sentAt: 0, expiresAt: 0, alive });

/** Wait this long before offering another code (3 an hour per identifier). */
const RESEND_AFTER_S = 60;

/** The only sentence after asking for a code: the same whether the account exists or not. */
export const RECOVERY_SENT = 'Si el dato corresponde a una cuenta, te enviamos un código por WhatsApp.';

export interface RecoveryProps {
  api: ApiClient;
  brand: AuthBrand;
  step: RecoveryStep;
  state: RecoveryState;
  onState: (s: RecoveryState) => void;
  go: (step: RecoveryStep, opts?: { replace?: boolean }) => void;
  /** Show the company field: the address does not name the company (development). */
  askTenant: boolean;
  /** Company slug to send when the field is not shown (from `?tenant=`); never in production. */
  tenant: string | null;
  large?: boolean;
  /** The flow started over (a reload, an expired step): why. */
  notice?: string | null;
  onSignIn: () => void;
}

/**
 * Password recovery without a session (ADR-015 §5.5): «Celular o correo» → the code sent to the
 * account's WhatsApp → a new password → done, signed out everywhere. The first answer never says
 * whether the account exists; ending it does not sign in (the person signs in with the new password).
 */
export function Recovery(props: RecoveryProps) {
  const { api, brand, state, onState, go, askTenant, large } = props;
  // once done, Back to an earlier step still shows the end (the flow cannot be repeated with its spent data)
  const step: RecoveryStep = state.done && props.step !== 'start' ? 'done' : props.step;
  const [tenantInput, setTenantInput] = useState(state.tenant ?? props.tenant ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [repeatError, setRepeatError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const now = useNow(1000);
  const waitS = Math.max(0, Math.ceil((state.sentAt + RESEND_AFTER_S * 1000 - now) / 1000));
  const leftS = Math.max(0, Math.floor((state.expiresAt - now) / 1000));
  const btn = `btn primary block ${large ? 'lg' : ''}`;

  const on = (fn: () => Promise<void> | void) => (e: FormEvent) => {
    e.preventDefault();
    void fn();
  };

  /** Asks for a code (first one or another one). The answer is the same for any identifier. */
  const request = async (identifier: string, tenant: string | null, again: boolean) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api.recovery.start(identifier, tenant);
      onState({ ...emptyRecovery(identifier, true), tenant, challengeId: r.challengeId, sentAt: Date.now(), expiresAt: Date.now() + r.expiresIn * 1000 });
      setCode('');
      // while a code is still valid the API may answer the same one without sending another (SEC-E3-1):
      // the sentence holds either way and still says nothing about whether the account exists
      if (again) setNotice('Recibimos tu solicitud otra vez. Si el dato corresponde a una cuenta, usa el último código que te llegó por WhatsApp.');
      else go('code');
    } catch (err) {
      setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!state.challengeId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api.recovery.verify(state.challengeId, code);
      onState({ ...state, grant: r.grant });
      setCode('');
      go('password');
    } catch (err) {
      setCode('');
      const c = errorCode(err);
      const attemptsLeft = c === 'OTP_INVALID' ? ((err as ApiError).details as { attemptsLeft?: unknown } | undefined)?.attemptsLeft : undefined;
      if (typeof attemptsLeft === 'number')
        setError(`${AUTH_ERRORS.OTP_INVALID.user} ${attemptsLeft === 1 ? 'Te queda 1 intento con este código.' : `Te quedan ${attemptsLeft} intentos con este código.`}`);
      else if (c === 'OTP_EXPIRED' || c === 'OTP_TOO_MANY_ATTEMPTS') {
        // this code is dead: another one can be requested right away
        onState({ ...state, sentAt: 0, expiresAt: 0 });
        setError(authMessage(err));
      } else {
        const s = retryAfterSeconds(err);
        setError(s ? `Demasiados intentos. Podrás intentarlo de nuevo en ${waitText(s)}.` : authMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (!state.grant) return;
    setPasswordError(null);
    setRepeatError(null);
    setError(null);
    if ([...password].length < PASSWORD_RULES.minLength) return setPasswordError(`Debe tener al menos ${PASSWORD_RULES.minLength} caracteres.`);
    if (password !== repeat) return setRepeatError('Las dos contraseñas no coinciden.');
    setBusy(true);
    try {
      await api.recovery.complete(state.grant, password);
      setPassword('');
      setRepeat('');
      onState({ ...emptyRecovery(state.identifier, true), done: true });
      go('done', { replace: true });
    } catch (err) {
      const c = errorCode(err);
      if (c === 'WEAK_PASSWORD') {
        // the same grant still works: the person picks another one
        const why = Array.isArray((err as ApiError).details) ? ((err as ApiError).details as Array<{ message?: unknown }>)[0]?.message : null;
        setPasswordError(typeof why === 'string' && why ? why : AUTH_ERRORS.WEAK_PASSWORD.user);
      } else if (c === 'RECOVERY_EXPIRED') {
        setPassword('');
        setRepeat('');
        onState({ ...emptyRecovery(state.identifier, true) });
        go('start', { replace: true });
        setNotice(AUTH_ERRORS.RECOVERY_EXPIRED.user);
      } else {
        const s = retryAfterSeconds(err);
        setError(s ? `Demasiados intentos. Podrás intentarlo de nuevo en ${waitText(s)}.` : authMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const shownNotice = notice ?? props.notice ?? null;

  if (step === 'start')
    return (
      <AuthCard
        key="start"
        brand={brand}
        title="Recupera tu contraseña"
        error={error}
        onSubmit={on(() => {
          const tenant = askTenant ? tenantInput.trim().toLowerCase() || null : props.tenant;
          return request(state.identifier.trim(), tenant, false);
        })}
      >
        {shownNotice && (
          <Notice tone="warn" urgent>
            {shownNotice}
          </Notice>
        )}
        <p style={{ margin: 0 }}>Escribe el celular o el correo con el que ingresas. Te enviaremos un código de 6 dígitos al WhatsApp de tu cuenta.</p>
        {askTenant && <TenantField value={tenantInput} onChange={setTenantInput} />}
        <IdentifierField value={state.identifier} onChange={(identifier) => onState({ ...state, identifier })} />
        <button className={btn} disabled={busy}>
          <MessageCircle size={18} aria-hidden /> {busy ? 'Enviando…' : 'Enviar código'}
        </button>
        <button type="button" className="btn ghost sm" onClick={props.onSignIn}>
          <ArrowLeft size={14} aria-hidden /> Volver al ingreso
        </button>
      </AuthCard>
    );

  if (step === 'code')
    return (
      <AuthCard key="code" brand={brand} title="Escribe el código" error={error} onSubmit={on(verify)}>
        <Notice tone="info">
          {shownNotice ?? RECOVERY_SENT}{' '}
          {leftS > 0 ? (
            <>
              El código vence en <span className="nowrap">{leftS > 90 ? waitText(leftS) : countdown(leftS)}</span>.
            </>
          ) : (
            'El código ya venció: pide otro.'
          )}
        </Notice>
        <CodeInput value={code} onChange={setCode} autoFocus label="Código de WhatsApp" hint="Los 6 dígitos del mensaje que te llegó." />
        <button className={btn} disabled={busy || code.length !== 6}>
          {busy ? 'Verificando…' : 'Verificar código'}
        </button>
        <button type="button" className="btn ghost sm" disabled={busy || waitS > 0} onClick={() => void request(state.identifier, state.tenant, true)}>
          Enviar otro código
        </button>
        {waitS > 0 && (
          <p className="small muted" style={{ margin: 0 }} role="status">
            ¿No te llegó? Podrás pedir otro en {countdown(waitS)}.
          </p>
        )}
        <button type="button" className="btn ghost sm" onClick={() => go('start')}>
          <ArrowLeft size={14} aria-hidden /> Cambiar el celular o correo
        </button>
      </AuthCard>
    );

  if (step === 'password')
    return (
      <AuthCard key="password" brand={brand} title="Elige una contraseña nueva" error={error} onSubmit={on(complete)}>
        <Notice tone="ok">Código verificado. Al guardar se cerrarán todas las sesiones abiertas de tu cuenta.</Notice>
        <NewPassword
          value={password}
          onChange={(v) => {
            setPassword(v);
            setPasswordError(null);
          }}
          repeat={repeat}
          onRepeat={(v) => {
            setRepeat(v);
            setRepeatError(null);
          }}
          error={passwordError}
          repeatError={repeatError}
          autoFocus
        />
        <button className={btn} disabled={busy}>
          {busy ? 'Guardando…' : 'Guardar contraseña'}
        </button>
      </AuthCard>
    );

  // done: no session was started; the person signs in with the new password
  return (
    <AuthCard key="done" brand={brand} title="Contraseña cambiada" done>
      <Notice tone="ok" urgent>
        Tu contraseña cambió. Por seguridad cerramos todas las sesiones abiertas de tu cuenta: ingresa de nuevo con tu contraseña nueva.
      </Notice>
      <button type="button" className={btn} onClick={props.onSignIn}>
        Ingresar
      </button>
    </AuthCard>
  );
}
